import React, { useState } from 'react';
import {
  decorateMentions,
  mentionNameMap,
  isMentionedMe,
} from '../../lib/message_text';
import ReactionPicker from './ReactionPicker';

function formatTime(createdAt) {
  const d = new Date(createdAt);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!n || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// @context TGW-1769 Slack TTP: アバター/名前クリックでプロフィール（/member/profile/:member_id）へ遷移。
//   member_id を持たないユーザー（退会等）はリンク化せずそのまま表示する。
function ProfileLink({ user, className, children }) {
  if (!user || !user.member_id) {
    return <span className={className}>{children}</span>;
  }
  return (
    <a className={className} href={`/member/profile/${user.member_id}`}>
      {children}
    </a>
  );
}

// @context TGW-1769 1メッセージ行。連続発言（isGrouped）はアバター/名前を省略して時刻のみ表示。
//   削除ボタンは「自分 or owner/manager」のみ表示（BEは正会員全員に許可するがUIで絞り誤削除を防ぐ）。
//   本文はBEの body_url_to_link（エスケープ+リンク化済み）に decorateMentions を重ねて表示する。
export default function MessageItem({
  message,
  currentUser,
  canManage,
  isGrouped,
  threadsEnabled,
  onOpenThread,
  onToggleReaction,
  onDelete,
  onRetry,
  // @context TGW-1860 自分宛メンションの黄色ハイライトを出す条件（未読レンジ内か）。
  //   未指定（スレッド等）は従来どおり常時ハイライトする。
  unread,
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [copied, setCopied] = useState(false);
  const user = message.user || {};

  // @context TGW-1769 Phase2 本文全文（メンション記法含むプレーンテキスト）をコピーする。
  //   HTML化済みの body_url_to_link ではなく raw の body を使う。
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.body || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // クリップボードが使えない環境（非https等）では何もしない
    }
  };
  const isMine = user.id === currentUser.id;
  const isDeleted = !!message.is_deleted;
  // @context TGW-1860 既読になったら黄色を消す。未読レンジ内（unread=true）のときだけ点ける。
  //   unread 未指定（スレッド）は従来どおり点ける。
  const mentionedMe =
    isMentionedMe(message, currentUser.id) && (unread ?? true);
  const sending = message.status === 'sending';
  const failed = message.status === 'failed';

  // @context TGW-1860 リアクション集計。固定5種ではなく、実際に付いている like_type
  //   （任意の絵文字）ごとに件数と自分が押したかを集計する。
  const reactionSummary = (() => {
    const map = new Map();
    (message.messages_like_users || []).forEach((l) => {
      const cur = map.get(l.like_type) || { count: 0, mine: false };
      cur.count += 1;
      if (l.user_id === currentUser.id) cur.mine = true;
      map.set(l.like_type, cur);
    });
    return Array.from(map.entries()).map(([type, v]) => ({ type, ...v }));
  })();

  const rowClasses = ['slackch-msg'];
  if (mentionedMe) rowClasses.push('slackch-msg--mentioned');
  if (sending) rowClasses.push('slackch-msg--sending');
  if (failed) rowClasses.push('slackch-msg--failed');

  // @context TGW-1853 本文の安定キー @tunagate_id を、閲覧者文脈で解決済みの表示名（message.mentions）
  //   に差し替える。送信者のあだ名は焼き込まれないため、受信者には自分の名前で「自分宛」として表示される。
  const bodyHtml = decorateMentions(
    message.body_url_to_link || message.body || '',
    mentionNameMap(message),
  );

  return (
    <div className={rowClasses.join(' ')}>
      {!isDeleted && (
        <div className="slackch-msg-toolbar">
          <button
            type="button"
            title="リアクション"
            aria-label="リアクション"
            onClick={() => setShowPicker((v) => !v)}
          >
            😊
          </button>
          {threadsEnabled && message.id && (
            <button
              type="button"
              title="スレッドで返信"
              aria-label="スレッドで返信"
              onClick={() => onOpenThread(message)}
            >
              💬
            </button>
          )}
          {message.body && (
            <button
              type="button"
              title="テキストをコピー"
              aria-label="テキストをコピー"
              onClick={handleCopy}
            >
              {copied ? '✅' : '📋'}
            </button>
          )}
          {(isMine || canManage) && message.id && (
            <button
              type="button"
              title="削除"
              aria-label="メッセージを削除"
              onClick={() => onDelete(message)}
            >
              🗑️
            </button>
          )}
        </div>
      )}
      {showPicker && (
        <ReactionPicker
          onPick={(type) => {
            setShowPicker(false);
            onToggleReaction(message, type);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div className="slackch-msg-gutter">
        {isGrouped ? (
          <span className="slackch-msg-time-hover">
            {formatTime(message.created_at)}
          </span>
        ) : (
          // Slack TTP: アバター/名前クリックでユーザープロフィールへ遷移（member_id が無い場合は非リンク）
          <ProfileLink user={user} className="slackch-msg-avatar-link">
            {user.avatar_thumb ? (
              <img
                className="slackch-msg-avatar"
                src={user.avatar_thumb}
                alt=""
              />
            ) : (
              <span className="slackch-msg-avatar slackch-msg-avatar--placeholder">
                {(user.familyname || '?').charAt(0)}
              </span>
            )}
          </ProfileLink>
        )}
      </div>

      <div className="slackch-msg-content">
        {!isGrouped && (
          <div className="slackch-msg-meta">
            <ProfileLink user={user} className="slackch-msg-name">
              {user.familyname || ''} {user.firstname || ''}
            </ProfileLink>
            <span className="slackch-msg-time">
              {formatTime(message.created_at)}
            </span>
          </div>
        )}
        {isDeleted ? (
          <div className="slackch-msg-body slackch-msg-body--deleted">
            削除されました
          </div>
        ) : (
          <>
            {/* body_url_to_link はBEでエスケープ済みのHTML（既存UIと同じ扱い） */}
            {/* eslint-disable-next-line react/no-danger */}
            <div
              className="slackch-msg-body"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
            {(message.attachments || []).length > 0 ? (
              <div className="slackch-msg-attachments">
                {(message.attachments || []).map((att) =>
                  att.is_image ? (
                    <a
                      key={att.id || att.url}
                      className="slackch-msg-image-link"
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        className="slackch-msg-picture"
                        src={att.thumb_url || att.url}
                        alt={att.file_name || ''}
                      />
                    </a>
                  ) : (
                    <a
                      key={att.id || att.url}
                      className="slackch-msg-file"
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={att.file_name || undefined}
                    >
                      <span className="slackch-msg-file-icon">📎</span>
                      <span className="slackch-msg-file-meta">
                        <span className="slackch-msg-file-name">
                          {att.file_name || 'ファイル'}
                        </span>
                        {att.file_size != null && (
                          <span className="slackch-msg-file-size">
                            {formatFileSize(att.file_size)}
                          </span>
                        )}
                      </span>
                    </a>
                  ),
                )}
              </div>
            ) : message.picture_medium || message.picture_url ? (
              <div className="slackch-msg-attachments">
                <img
                  className="slackch-msg-picture"
                  src={message.picture_medium || message.picture_url}
                  alt=""
                />
              </div>
            ) : null}
            {/* @context TGW-2018 リンクウィジェット（OGPプレビュー）。BEが取得成功分のみ
                link_previews に載せる（title/description/image_url はBEでプレーンテキスト化済み） */}
            {(message.link_previews || []).length > 0 && (
              <div className="slackch-msg-link-previews">
                {(message.link_previews || []).map((lp) => (
                  <a
                    key={lp.url}
                    className="slackch-link-card"
                    href={lp.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="slackch-link-card-text">
                      {lp.site_name && (
                        <span className="slackch-link-card-site">
                          {lp.site_name}
                        </span>
                      )}
                      <span className="slackch-link-card-title">
                        {lp.title}
                      </span>
                      {lp.description && (
                        <span className="slackch-link-card-desc">
                          {lp.description}
                        </span>
                      )}
                    </span>
                    {lp.image_url && (
                      <img
                        className="slackch-link-card-image"
                        src={lp.image_url}
                        alt=""
                        loading="lazy"
                      />
                    )}
                  </a>
                ))}
              </div>
            )}
          </>
        )}

        {failed && (
          <button
            type="button"
            className="slackch-msg-retry"
            onClick={() => onRetry(message.client_id)}
          >
            送信に失敗しました。タップで再送
          </button>
        )}

        {reactionSummary.length > 0 && (
          <div className="slackch-msg-reactions">
            {reactionSummary.map((r) => (
              <button
                key={r.type}
                type="button"
                className={`slackch-reaction-chip ${r.mine ? 'slackch-reaction-chip--mine' : ''}`}
                onClick={() => onToggleReaction(message, r.type)}
              >
                {r.type} {r.count}
              </button>
            ))}
          </div>
        )}

        {threadsEnabled && message.reply_count > 0 && (
          <button
            type="button"
            className="slackch-thread-summary"
            onClick={() => onOpenThread(message)}
          >
            <span className="slackch-thread-avatars">
              {(message.thread_participants || []).map((p) =>
                p.avatar_thumb ? (
                  <img key={p.id} src={p.avatar_thumb} alt="" />
                ) : (
                  <span key={p.id} className="slackch-thread-avatar-fallback">
                    👤
                  </span>
                ),
              )}
            </span>
            <span className="slackch-thread-summary-count">
              {message.reply_count}件の返信
            </span>
            {message.last_reply_at && (
              <span className="slackch-thread-summary-time">
                最終 {formatTime(message.last_reply_at)}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
